import { isArray } from "lodash-es";
import { format } from "date-fns";
import { AppField, Fields, ID, Preferences, SimpleRecord, Tag } from "@smartsuite/types";
import { getFieldLogic } from "@smartsuite/fields-logic/lib/field-type-registry/fields-logic-registry";
import { formatDisplayDuration } from "@smartsuite/fields-logic/lib/utils/duration";
import { dateValueToDate } from "@smartsuite/fields-logic/lib/utils/date";

import { BaseRecord } from "../../../../types/form";

export const getDisplayValue = (
  record: SimpleRecord,
  fieldSlug: string,
  fieldsToShow: { [key: string]: AppField },
  tags?: Tag[],
  relatedRecords?: BaseRecord[],
  isLookup?: boolean,
  userPreferences?: Preferences
): string | null => {
  if (fieldSlug.includes(".")) {
    return getNestedDisplayValue(record, fieldSlug, fieldsToShow, relatedRecords);
  }

  const field = fieldsToShow[fieldSlug];
  const rawValue = record[fieldSlug];
  if (rawValue == null) {
    return null;
  }
  return getFieldValue(rawValue, fieldSlug, field, tags, relatedRecords, isLookup, userPreferences);
};

const getNestedDisplayValue = (
  record: SimpleRecord,
  fieldSlug: string,
  fieldsToShow: { [key: string]: AppField },
  relatedRecords?: BaseRecord[]
): string | null => {
  const [mainFieldSlug, nestedFieldSlug] = fieldSlug.split(".");
  const field = fieldsToShow[fieldSlug];
  const fieldType = field?.field_type;

  if (fieldType === "fullnamefield") {
    return getFullNameNestedValue(record, mainFieldSlug, nestedFieldSlug, field);
  }

  if (fieldType === "duedatefield") {
    return getDueDateNestedValue(record, mainFieldSlug, nestedFieldSlug);
  }

  if (fieldType === "dependencyfield" || fieldType === "dependencyrelationfield") {
    return getDependencyNestedValue(record, mainFieldSlug, nestedFieldSlug, relatedRecords);
  }

  return getGenericNestedValue(record, mainFieldSlug, nestedFieldSlug);
};

const getFullNameNestedValue = (
  record: SimpleRecord,
  mainFieldSlug: string,
  nestedFieldSlug: string,
  field: AppField
): string | null => {
  const recordValue = record?.[mainFieldSlug]?.[nestedFieldSlug];
  if (nestedFieldSlug === "title" && recordValue) {
    const titleChoices = field.params.choices;
    return titleChoices?.find((choice) => choice.value === recordValue)?.label ?? null;
  }
  return recordValue;
};

const getDueDateNestedValue = (
  record: SimpleRecord,
  mainFieldSlug: string,
  nestedFieldSlug: string
): string | null => {
  const nested = record?.[mainFieldSlug]?.[nestedFieldSlug];
  if (!nested?.date) {
    return null;
  }
  return formatDateTime(nested.date, !!nested.include_time);
};

const getDependencyNestedValue = (
  record: SimpleRecord,
  mainFieldSlug: string,
  nestedFieldSlug: string,
  relatedRecords?: BaseRecord[]
): string | null => {
  const items = record?.[mainFieldSlug]?.[nestedFieldSlug];
  if (!Array.isArray(items) || items.length === 0) {
    return null;
  }
  return formatDependencyItems(items, relatedRecords);
};

const getGenericNestedValue = (
  record: SimpleRecord,
  mainFieldSlug: string,
  nestedFieldSlug: string
): string | null => {
  const nestedVal = record?.[mainFieldSlug]?.[nestedFieldSlug];
  // The fallthrough handles any nested value whose sub-field type wasn't explicitly matched above
  // (e.g. a duedatefield sub-field "from_date" exposed via lookup has field_type "datefield",
  // not "duedatefield", so the explicit branch above is skipped). Detect the { date, include_time }
  // shape here so callers don't need their own workarounds.
  if (nestedVal && typeof nestedVal === "object" && "date" in nestedVal) {
    const { date, include_time: includeTime } = nestedVal as {
      date?: string;
      include_time?: boolean;
    };
    return date ? formatDateTime(date, !!includeTime) : null;
  }
  return nestedVal == null ? null : String(nestedVal);
};

const ARRAY_FIELD_TYPES = new Set<Fields>([
  "lookupfield",
  "rollupfield",
  "linkedrecordfield",
  "userfield",
  "teamfield",
  "tagsfield",
  "dependencyrelationfield",
  "phonefield",
  "linkfield",
]);

export const getFieldValue = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  recordValue: { [key: string]: any },
  fieldSlug: string,
  field: AppField,
  tags: Tag[] = [],
  relatedRecords?: BaseRecord[],
  isLookup?: boolean,
  userPreferences?: Preferences
): string | null => {
  const fieldLogic = getFieldLogic(field.field_type);

  // When target_field_structure is resolved to the final type (e.g. singleselectfield)
  // but the actual value is a nested lookup array, flatten and process each atomic value.
  if (isLookup && isArray(recordValue) && !ARRAY_FIELD_TYPES.has(field.field_type)) {
    return flattenLookupArray(recordValue, fieldSlug, field, tags, relatedRecords, userPreferences);
  }

  switch (field.field_type) {
    case "linkedrecordfield":
    case "userfield":
      return formatRelatedRecords(recordValue, relatedRecords);
    case "teamfield":
      return formatTeamValue(recordValue, relatedRecords);
    case "buttonfield":
      return field.params.button_label ?? null;
    case "socialnetworkfield":
      return "socialnetworkfield";
    case "tagsfield":
      return formatTags(recordValue as string[], tags);
    case "checklistfield":
      return formatChecklist(recordValue);
    case "currencyfield":
      return formatCurrency(recordValue, field);
    case "numberfield":
    case "numbersliderfield":
    case "ratingfield":
      return formatNumericValue(recordValue);
    case "daterangefield":
    case "duedatefield":
      return formatDateRange(recordValue);
    case "firstcreatedfield":
    case "lastupdatedfield":
      return formatDateTime(recordValue?.on, true);
    case "subitemsfield":
      return recordValue?.count ?? 0;
    case "timetrackingfield":
      return recordValue?.total_duration
        ? formatDisplayDuration(recordValue.total_duration, "compact", "hh:mm")
        : null;
    case "votefield":
      return recordValue?.total_votes?.toString();
    case "timefield":
      return typeof recordValue === "string" ? formatUtcTime(recordValue) : null;
    case "datefield":
      return recordValue?.date
        ? formatDateTime(recordValue?.date, !!recordValue?.include_time)
        : null;
    case "dependencyfield":
      return formatDependency(recordValue, isLookup);
    case "dependencyrelationfield":
      return formatDependencyRelation(recordValue, relatedRecords);
    case "lookupfield":
    case "rollupfield":
      return formatLookupOrRollup(
        recordValue,
        fieldSlug,
        field,
        tags,
        relatedRecords,
        userPreferences
      );
    case "formulafield":
      return formatFormula(recordValue, fieldSlug, field, tags, relatedRecords, userPreferences);
    // Numeric/locale-aware fields (percent, percent complete, count) and any other field
    // whose default display rendering relies on userPreferences for locale-correct number
    // formatting. Without userPreferences, getDecimalSeparator falls back to the literal
    // string ".|," and produces broken output like "4.|,0".
    default:
      return fieldLogic.getAggregateDisplayValue(recordValue, field, userPreferences);
  }
};

const flattenLookupArray = (
  recordValue: unknown,
  fieldSlug: string,
  field: AppField,
  tags: Tag[],
  relatedRecords: BaseRecord[] | undefined,
  userPreferences?: Preferences
): string | null => {
  const flatValues = (recordValue as unknown[]).flat(Infinity).filter((v) => v != null);
  if (flatValues.length === 0) return null;
  return (
    flatValues
      .map((v) =>
        getFieldValue(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          v as any,
          fieldSlug,
          field,
          tags,
          relatedRecords,
          true,
          userPreferences
        )
      )
      .filter(Boolean)
      .join(", ") || null
  );
};

const formatRelatedRecords = (
  recordValue: unknown,
  relatedRecords: BaseRecord[] | undefined
): string => {
  // recordValue may be deeply nested (e.g. [[["id"]]] from lookup API) — flatten to strings first
  return (Array.isArray(recordValue) ? (recordValue as unknown[]).flat(Infinity) : [])
    .filter((v): v is ID => typeof v === "string")
    .reduce((acc: string[], relatedRecordID: ID) => {
      const relatedRecord = relatedRecords?.find((r) => r.id === relatedRecordID);
      if (!relatedRecord) {
        return acc;
      }
      return [...acc, relatedRecord.title];
    }, [])
    .join(", ");
};

const formatTeamValue = (
  recordValue: unknown,
  relatedRecords: BaseRecord[] | undefined
): string => {
  // Team values may be resolved objects ({ id, name }) or raw ID strings
  return (Array.isArray(recordValue) ? (recordValue as unknown[]).flat(Infinity) : [])
    .map((item) => {
      if (typeof item === "object" && item !== null && "name" in item) {
        return (item as { name: string }).name;
      }
      if (typeof item === "string") {
        return relatedRecords?.find((r) => r.id === item)?.title;
      }
      return null;
    })
    .filter(Boolean)
    .join(", ");
};

const formatNumericValue = (recordValue: unknown): string | null => {
  if (typeof recordValue === "number") {
    return String(recordValue);
  }
  if (typeof recordValue === "string") {
    return recordValue;
  }
  return null;
};

const formatTags = (tagsValue: string[], tags: Tag[]): string => {
  return tagsValue
    .map((tagValue) => tags.find((tag) => tag.id === tagValue)?.label)
    .filter(Boolean)
    .join(", ");
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const formatChecklist = (recordValue: any): string => {
  const totalItems = recordValue?.total_items ?? 0;
  const completedItems = recordValue?.completed_items ?? 0;
  const progress = Math.round(totalItems > 0 ? (completedItems / totalItems) * 100 : 0);
  return `${completedItems} of ${totalItems} (${progress}%)`;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const formatCurrency = (recordValue: any, field: AppField): string | null => {
  const value = Number(recordValue);
  if (Number.isNaN(value)) {
    return null;
  }
  return `${value.toFixed(field.params?.precision ?? 2)} ${field.params?.currency}`;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const formatDateRange = (recordValue: any): string | null => {
  if (!recordValue?.from_date || !recordValue?.to_date) {
    return null;
  }
  if (!recordValue?.from_date?.date) {
    return recordValue?.to_date?.date
      ? formatDateTime(recordValue.to_date.date, recordValue.to_date.include_time)
      : null;
  }
  return `${formatDateTime(
    recordValue?.from_date?.date,
    recordValue?.from_date?.include_time
  )} - ${formatDateTime(recordValue?.to_date?.date, recordValue?.to_date?.include_time)}`;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const formatDependency = (recordValue: any, isLookup: boolean | undefined): string => {
  const predCount = recordValue?.predecessor?.length ?? 0;
  const sucCount = recordValue?.successor?.length ?? 0;
  if (isLookup) {
    const predLabel = predCount === 1 ? "Predecessor" : "Predecessors";
    const sucLabel = sucCount === 1 ? "Successor" : "Successors";
    return `${predLabel}: ${predCount} ${sucLabel}: ${sucCount}`;
  }
  return `Pred...: ${predCount} Suc...: ${sucCount}`;
};

const formatDependencyRelation = (
  recordValue: unknown,
  relatedRecords: BaseRecord[] | undefined
): string | null => {
  const items = Array.isArray(recordValue) ? recordValue : [];
  if (items.length === 0) return null;
  return formatDependencyItems(items, relatedRecords) || null;
};

const formatDependencyItems = (
  items: Array<{ record: string | { id: string; title: string } }>,
  relatedRecords: BaseRecord[] | undefined
): string => {
  return items
    .map((item) => {
      if (typeof item.record === "object" && item.record?.title) {
        return item.record.title;
      }
      const recordId = typeof item.record === "string" ? item.record : item.record?.id;
      return relatedRecords?.find((r) => r.id === recordId)?.title || "";
    })
    .filter(Boolean)
    .join(", ");
};

const formatLookupOrRollup = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  recordValue: any,
  fieldSlug: string,
  field: AppField,
  tags: Tag[],
  relatedRecords: BaseRecord[] | undefined,
  userPreferences?: Preferences
): string | null => {
  const targetFieldStructure = field.params?.target_field_structure;
  if (!targetFieldStructure) {
    return null;
  }
  if (isArray(recordValue)) {
    return recordValue
      .flat()
      .map((currentValue) =>
        getFieldValue(
          currentValue,
          fieldSlug,
          targetFieldStructure,
          tags,
          relatedRecords,
          undefined,
          userPreferences
        )
      )
      .join(", ");
  }
  return getFieldValue(
    recordValue,
    fieldSlug,
    targetFieldStructure,
    tags,
    relatedRecords,
    undefined,
    userPreferences
  );
};

const formatFormula = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  recordValue: any,
  fieldSlug: string,
  field: AppField,
  tags: Tag[],
  relatedRecords: BaseRecord[] | undefined,
  userPreferences?: Preferences
): string | null => {
  const targetField = field.params?.target_field_structure;
  if (targetField?.field_type === "currencyfield") {
    return getFieldValue(
      recordValue,
      fieldSlug,
      targetField,
      tags,
      relatedRecords,
      undefined,
      userPreferences
    );
  }
  return getFieldLogic(field.field_type).getAggregateDisplayValue(
    recordValue,
    field,
    userPreferences
  );
};

/**
 * Formats a stored date value.
 * A date-only value is stored as UTC start of day, and `date-fns` `format` reads local calendar
 * fields, so a plain `new Date()` prints the previous day for any viewer behind UTC. Read such a
 * value in UTC to keep the stored calendar day. A value that carries a time is a real instant, so
 * it stays in the viewer's timezone.
 */
const formatDateTime = (date: string, includeTime: boolean): string => {
  if (includeTime) {
    return format(new Date(date), "yyyy-MM-dd HH:mm");
  }
  const parsedDate = dateValueToDate({ date, include_time: false });
  return parsedDate ? format(parsedDate, "yyyy-MM-dd") : "";
};

/**
 * Extracts time from a UTC ISO string without timezone conversion.
 * Time fields use a dummy date (0001-01-01) with the time portion as the value.
 * Parsing via `new Date()` would convert to local timezone, shifting the time.
 * Uses the browser locale to determine 12h vs 24h format.
 */
const UTC_TIME_REGEX = /T(\d{2}):(\d{2})/;

const formatUtcTime = (isoString: string): string | null => {
  const match = UTC_TIME_REGEX.exec(isoString);
  if (!match) return null;
  const hours = Number.parseInt(match[1], 10);
  const minutes = match[2];
  const is12h = new Intl.DateTimeFormat(navigator.language, { hour: "numeric" }).resolvedOptions()
    .hour12;
  if (is12h) {
    const ampm = hours >= 12 ? "PM" : "AM";
    const displayHour = hours % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  }
  return `${hours.toString().padStart(2, "0")}:${minutes}`;
};
